import { formatDistanceMeters as formatDistanceMetersWithUnit } from './distanceUtils';

const normalizeDurationLanguage = (options = {}) => {
  const language = typeof options === 'string'
    ? options
    : options.language ?? options.lng ?? options.locale;

  return typeof language === 'string' ? language.toLowerCase() : '';
};

const translateKey = (t, key, fallback) => {
  if (typeof t !== 'function') return fallback;

  const translated = t(key, { defaultValue: fallback });
  if (typeof translated === 'string' && translated && translated !== key) return translated;

  const legacyTranslated = t(key, fallback);
  if (typeof legacyTranslated === 'string' && legacyTranslated && legacyTranslated !== key) {
    return legacyTranslated;
  }

  return fallback;
};

const isChineseDurationContext = (t, options) => {
  const language = normalizeDurationLanguage(options);
  if (/^(cn|zh)([-_]|$)/.test(language)) return true;

  const minuteText = translateKey(t, 'min', '');
  return minuteText === '分' || minuteText.includes('分钟');
};

const translateDurationUnit = (t, key, fallback, options = {}) => {
  const translated = translateKey(t, key, fallback);
  if (translated !== fallback) return translated;

  if (isChineseDurationContext(t, options)) {
    return key === 'durationMinuteShort' ? '分' : '秒';
  }

  return fallback;
};

export const buildSegmentKey = (fromWaypointId, toWaypointId) => (
  `${Number(fromWaypointId)}-${Number(toWaypointId)}`
);

export const getSegmentKey = (segment = {}) => buildSegmentKey(
  segment.fromWaypointId ?? segment.from_waypoint_id ?? segment.fromWaypoint?.id,
  segment.toWaypointId ?? segment.to_waypoint_id ?? segment.toWaypoint?.id
);

export const isSequentialToStateToken = (isSequential) => {
  // Shape-aware keys are also mutation authority markers. Unknown shape must
  // fail closed instead of silently targeting the direct `is_sequential = 0`
  // row.
  if (typeof isSequential !== 'boolean') {
    throw new Error('isSequential is required for segment shape keys');
  }

  return isSequential ? 'is_sequential_1' : 'is_sequential_0';
};

export const formatDurationSeconds = (seconds, t = null, options = {}) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '--';

  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const minUnit = translateDurationUnit(t, 'durationMinuteShort', 'm', options);
  const secUnit = translateDurationUnit(t, 'durationSecondShort', 's', options);

  if (mins === 0) return `${secs}${secUnit}`;
  if (secs === 0) return `${mins}${minUnit}`;
  return `${mins}${minUnit} ${secs}${secUnit}`;
};

export const formatDistanceMeters = (meters, distanceUnit = 'km', t = null, options = {}) => (
  formatDistanceMetersWithUnit(meters, distanceUnit, t, { compact: true, ...options })
);

export const formatDurationMinutes = (minutes, t = null) => {
  const totalMinutes = Number(minutes);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '-';

  if (totalMinutes < 60) {
    return `${Math.round(totalMinutes)} ${t ? t('min') : 'min'}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainderMinutes = Math.round(totalMinutes % 60);
  if (remainderMinutes > 0) {
    return `${hours} ${t ? t('hr') : 'hr'} ${remainderMinutes} ${t ? t('min') : 'min'}`;
  }
  return `${hours} ${t ? t('hr') : 'hr'}`;
};

export default {
  buildSegmentKey,
  getSegmentKey,
  isSequentialToStateToken,
  formatDurationSeconds,
  formatDistanceMeters,
  formatDurationMinutes
};
